import * as ko from "knockout"
import {IPasswordPolicy} from "../../../index"
import {PASSWORD_MINIMUM_LOWERCASE_LETTERS} from "../../../constants"
import {nsResetPassword} from "../nsResetPassword";
import {hasOnlyAsciiLetters} from "./hasOnlyAsciiLetters";

interface IParams {
    observableInputs: IObservableInputs
    passwordPolicy: IPasswordPolicy
    mode: TPasswordPageMode
}

export class FormValidator {
    private observableInputs: IObservableInputs
    private passwordPolicy: IPasswordPolicy
    private readonly mode: TPasswordPageMode

    constructor(params: IParams) {
        this.observableInputs = params.observableInputs
        this.passwordPolicy = params.passwordPolicy
        this.mode = params.mode
    }

    public isFormValid = ko.pureComputed((): boolean => {
        return this.isMinLength () &&
        this.isMaxLength () &&
        this.isWithinLength () &&
        this.isLowerCase () &&
        this.isUpperCase () &&
        this.isNumbers () && 
        this.isSpecial () &&
        this.hasOnlyAsciiLetters () &&
        this.isNewAndRepeatSame () &&
        this.isOldValid ()
    })

    public isMinLength = ko.pureComputed((): boolean => {
        return this.observableInputs.new().length >= this.passwordPolicy.minimumlength
    })

    public isMaxLength = ko.pureComputed((): boolean => {
        return this.observableInputs.new().length <= this.passwordPolicy.maximumlength
    })

    public isWithinLength = ko.pureComputed((): boolean => {
        return this.isMinLength() && this.isMaxLength()
    })

    public isLowerCase = ko.pureComputed((): boolean => {
        const str = this.observableInputs.new()
        const lowercaseLetters = str.match(/[a-z]/g);
        const num = lowercaseLetters ? lowercaseLetters.length : 0;
        return num >= PASSWORD_MINIMUM_LOWERCASE_LETTERS
    })

    public isUpperCase = ko.pureComputed((): boolean => {
        const str = this.observableInputs.new()
        const uppercaseLetters = str.match(/[A-Z]/g);
        const num = uppercaseLetters ? uppercaseLetters.length : 0;
        return num >= this.passwordPolicy.nrupper
    })

    public isNumbers = ko.pureComputed((): boolean => {
        const str = this.observableInputs.new()
        const numerals = str.match(/[0-9]/g);
        const num = numerals ? numerals.length : 0;
        return num >= this.passwordPolicy.nrnumeral
    })

    public isSpecial = ko.pureComputed((): boolean => {
        const str = this.observableInputs.new()
        const matches = str.match(/[!@#$%&*.?]/g);
        const num = matches ? matches.length : 0
        return num >= this.passwordPolicy.nrspecial
    })

    public hasOnlyAsciiLetters = ko.pureComputed((): boolean => {
        return hasOnlyAsciiLetters(this.observableInputs.new())
    })

    public isNewAndRepeatSame: KnockoutComputed<boolean> = ko.pureComputed(() => {
        const newStr = this.observableInputs.new()
        const repeatStr = this.observableInputs.repeat()
        return newStr !== '' && repeatStr !== '' && newStr === repeatStr;
    })

    /** True only when both fields have a value and they differ. */
    public hasNewAndRepeatMismatch: KnockoutComputed<boolean> = ko.pureComputed(() => {
        const newStr = this.observableInputs.new()
        const repeatStr = this.observableInputs.repeat()
        return newStr !== '' && repeatStr !== '' && newStr !== repeatStr;
    })

    public isOldValid(): boolean {
        if (this.mode === 'admin' || !nsResetPassword.get_changepasswordrequiresoldpassword()) {
            return true
        }
        return this.observableInputs.old() != ''
    }

    public isNewValid(): boolean {
        return this.isMinLength() &&
            this.isMaxLength() &&
            this.isWithinLength() &&
            this.isLowerCase() &&
            this.isUpperCase() &&
            this.isNumbers() &&
            this.isSpecial() &&
            this.hasOnlyAsciiLetters()
    }

    public isRepeatValid(): boolean {
        return this.isNewAndRepeatSame()
    }

}
